import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/clinic/Navbar";
import Footer from "@/components/clinic/Footer";
import { applyJsonLd, applySeo, SITE_URL } from "@/lib/seo";

const Terms = () => {
  useEffect(() => {
    applySeo({
      title: "Terms of Service — Blepharoplasty London | Dr Vahe Clinic",
      description:
        "Booking conditions, promotional rates, payments and cancellation policy for blepharoplasty at Dr Vahe Clinic in central London.",
      path: "/terms",
      ogType: "article",
    });

    const cleanup = applyJsonLd("breadcrumbs-terms", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Terms of Service", item: `${SITE_URL}/terms` },
      ],
    });

    return () => cleanup();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <article className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <Link
            to="/"
            className="text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70 transition-colors"
          >
            ← Back to clinic
          </Link>

          <header className="mt-8 mb-12">
            <span className="text-[10px] tracking-[0.35em] uppercase text-accent font-medium">
              Legal
            </span>
            <h1 className="font-display text-4xl md:text-5xl mt-4 text-foreground">
              Terms of <span className="italic">Service</span>
            </h1>
            <p className="mt-4 text-xs tracking-[0.2em] uppercase text-muted-foreground">
              Last updated: April 2026
            </p>
          </header>

          <div className="space-y-10 text-foreground/85 font-light leading-relaxed">
            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">1. About these terms</h2>
              <p>
                These terms govern your use of the Dr Vahe Clinic website and any enquiry,
                consultation or treatment booked through it. By submitting an enquiry you confirm
                that you have read and accept these terms.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">2. Consultations and treatment</h2>
              <p>
                All surgical and aesthetic procedures are subject to a pre-operative consultation
                and medical assessment. Suitability for blepharoplasty is determined by Dr Vahe at
                the time of consultation. We reserve the right to decline treatment where it is
                not in your clinical best interests.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">3. Promotional rates</h2>
              <p>
                Preferred rates advertised on this site are subject to consultation, clinical
                suitability and availability. The applicable fee is confirmed in writing before
                a deposit is taken. The promotion cannot be combined with other offers.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">4. Fees and payment</h2>
              <p>
                Fees are confirmed in writing following your consultation. A non-refundable
                deposit is required to secure your surgical date, with the balance payable in
                advance of surgery. Instalment plans are available through Klarna, subject to
                Klarna's eligibility checks and terms.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">5. Cancellations and rescheduling</h2>
              <p>
                We ask for at least 14 days' notice to reschedule or cancel your surgical date so
                we can offer the slot to another patient. Cancellations made within 14 days may
                forfeit the deposit. Any promotional rate remains subject to the written terms
                provided when your booking is confirmed.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">6. Results and expectations</h2>
              <p>
                Surgical and aesthetic results vary between individuals. While we strive for the
                very best outcomes, no specific result can be guaranteed. Risks, recovery and
                realistic expectations are discussed in detail at consultation.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">7. Website content</h2>
              <p>
                Information on this website is provided for general guidance only and does not
                constitute medical advice. Imagery, copy and branding are the property of Dr Vahe
                Clinic and may not be reproduced without permission.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">8. Governing law</h2>
              <p>
                These terms are governed by the laws of England and Wales. Any disputes shall be
                subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">
                9. Service area — Blepharoplasty in London
              </h2>
              <p>
                These terms apply to <strong>blepharoplasty (eyelid surgery)</strong> and related
                consultations provided by Dr Vahe Clinic in <strong>central London</strong>,
                including <strong>upper blepharoplasty</strong>, <strong>lower blepharoplasty</strong>{" "}
                and combined upper-and-lower procedures. The clinic welcomes patients from across
                Greater London — Mayfair, Marylebone, the Harley Street area, Knightsbridge,
                Chelsea, Kensington and the City — as well as Surrey, Hertfordshire, Essex and
                visitors from outside the UK.
              </p>
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
